import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Settings, 
  Palette, 
  Grid3x3, 
  FileText, 
  Eye, 
  EyeOff, 
  Edit, 
  Trash2, 
  Plus, 
  Save, 
  Upload, 
  Link, 
  Facebook, 
  Twitter, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  Image as ImageIcon,
  Layout,
  Sparkles,
  Crown,
  Code,
  Monitor,
  Store,
  Search,
  // Icônes pour catégories
  Package, ShoppingBag, ShoppingCart, Gift, Tag, CreditCard, Wallet,
  Smartphone, Laptop, Tablet, Watch, Headphones, Camera, Tv, Speaker, Usb,
  Shirt, Glasses, Gem, Scissors,
  Home, Sofa, Bed, Lamp, Hammer, Wrench, Paintbrush, Drill, Leaf, Trees, Flower,
  Baby,
  Car, Truck, Bike, Fuel,
  Dumbbell, Trophy, Target, Mountain, Tent, Waves, Wind,
  Gamepad2,
  Book, BookOpen, Newspaper, PenTool, FileText as FileTextIcon, Music,
  Heart, HeartPulse, Activity, Pill, Stethoscope, Syringe, Droplet,
  Coffee, Pizza, UtensilsCrossed, Apple,
  Star, Zap, Award, BadgeCheck, CircleDot, Hexagon
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { useCategoryTranslation } from "@/hooks/useCategoryTranslation";
import { BecomeVendorEditor } from "@/components/admin/BecomeVendorEditor";
import { PageEditor } from "@/components/admin/PageEditor";
import { HeroImageManager } from "@/components/admin/HeroImageManager";
import { LegalPageEditor } from "@/components/admin/LegalPageEditor";
import { autoTranslateCategoryName, generateMissingTranslations } from "@/utils/categoryTranslations";
import '@/assets/react-quill.css';

// Lazy import pour éviter les problèmes SSR
const ReactQuill = lazy(() => import('react-quill'));

// Mapping des icônes pour les catégories
const iconMap: { [key: string]: any } = {
  // Commerce & Shopping
  Package, ShoppingBag, ShoppingCart, Store, Gift, Tag, CreditCard, Wallet,
  // Électronique & Tech
  Smartphone, Laptop, Monitor, Tablet, Watch, Headphones, Camera, Tv, Speaker, Usb,
  // Mode & Accessoires
  Shirt, Glasses, Crown, Gem, Scissors,
  // Maison & Jardin
  Home, Sofa, Bed, Lamp, Hammer, Wrench, Paintbrush, Drill, Leaf, Trees, Flower,
  // Enfants & Bébés
  Baby,
  // Auto & Moto
  Car, Truck, Bike, Fuel,
  // Sport & Loisirs
  Dumbbell, Trophy, Target, Mountain, Tent, Waves, Wind,
  // Jeux & Jouets
  Gamepad2, Sparkles,
  // Livres & Culture
  Book, BookOpen, Newspaper, PenTool, FileTextIcon, Music,
  // Santé & Bien-être
  Heart, HeartPulse, Activity, Pill, Stethoscope, Syringe, Droplet,
  // Alimentation
  Coffee, Pizza, UtensilsCrossed, Apple,
  // Général
  Star, Zap, Award, BadgeCheck, CircleDot, Hexagon
};

interface CMSManagerProps {
  siteSettings: any[];
  categories: any[];
  footerSections: any[];
  cmsContent: any[];
  onRefresh: () => void;
  user: any;
}

const availableIcons = [
  // Commerce & Shopping
  'Package', 'ShoppingBag', 'ShoppingCart', 'Store', 'Gift', 'Tag', 'CreditCard', 'Wallet',
  // Électronique & Tech
  'Smartphone', 'Laptop', 'Monitor', 'Tablet', 'Watch', 'Headphones', 'Camera', 'Tv', 'Speaker', 'Usb',
  // Mode & Accessoires
  'Shirt', 'Glasses', 'Crown', 'Gem', 'Scissors',
  // Maison & Jardin
  'Home', 'Sofa', 'Bed', 'Lamp', 'Hammer', 'Wrench', 'Paintbrush', 'Drill', 'Leaf', 'Trees', 'Flower',
  // Enfants & Bébés
  'Baby', 'Heart', 'Star',
  // Auto & Moto
  'Car', 'Truck', 'Bike', 'Fuel', 'Wrench',
  // Sport & Loisirs
  'Dumbbell', 'Trophy', 'Target', 'Mountain', 'Tent', 'Waves', 'Wind',
  // Jeux & Jouets
  'Gamepad2', 'Sparkles', 'Star', 'Trophy',
  // Livres & Culture
  'Book', 'BookOpen', 'Newspaper', 'PenTool', 'FileTextIcon', 'Music',
  // Santé & Bien-être
  'Heart', 'HeartPulse', 'Activity', 'Pill', 'Stethoscope', 'Syringe', 'Droplet',
  // Alimentation
  'Coffee', 'Pizza', 'UtensilsCrossed', 'Apple',
  // Général
  'Star', 'Zap', 'Award', 'BadgeCheck', 'CircleDot', 'Hexagon'
];

const colorGradients = [
  // Couleurs classiques
  { name: 'Bleu', from: 'blue-500', to: 'blue-600', colors: { from: '#3B82F6', to: '#2563EB' } },
  { name: 'Rose', from: 'pink-500', to: 'pink-600', colors: { from: '#EC4899', to: '#DB2777' } },
  { name: 'Vert', from: 'green-500', to: 'green-600', colors: { from: '#22C55E', to: '#16A34A' } },
  { name: 'Violet', from: 'purple-500', to: 'purple-600', colors: { from: '#A855F7', to: '#9333EA' } },
  { name: 'Orange', from: 'orange-500', to: 'orange-600', colors: { from: '#F97316', to: '#EA580C' } },
  { name: 'Rouge', from: 'red-500', to: 'red-600', colors: { from: '#EF4444', to: '#DC2626' } },
  { name: 'Indigo', from: 'indigo-500', to: 'indigo-600', colors: { from: '#6366F1', to: '#4F46E5' } },
  { name: 'Emeraude', from: 'emerald-500', to: 'emerald-600', colors: { from: '#10B981', to: '#059669' } },
  { name: 'Jaune', from: 'yellow-500', to: 'yellow-600', colors: { from: '#EAB308', to: '#CA8A04' } },
  { name: 'Cyan', from: 'cyan-500', to: 'cyan-600', colors: { from: '#06B6D4', to: '#0891B2' } },
  // Couleurs supplémentaires
  { name: 'Teal', from: 'teal-500', to: 'teal-600', colors: { from: '#14B8A6', to: '#0D9488' } },
  { name: 'Lime', from: 'lime-500', to: 'lime-600', colors: { from: '#84CC16', to: '#65A30D' } },
  { name: 'Amber', from: 'amber-500', to: 'amber-600', colors: { from: '#F59E0B', to: '#D97706' } },
  { name: 'Fuchsia', from: 'fuchsia-500', to: 'fuchsia-600', colors: { from: '#D946EF', to: '#C026D3' } },
  { name: 'Sky', from: 'sky-500', to: 'sky-600', colors: { from: '#0EA5E9', to: '#0284C7' } },
  { name: 'Slate', from: 'slate-500', to: 'slate-600', colors: { from: '#64748B', to: '#475569' } },
  { name: 'Zinc', from: 'zinc-500', to: 'zinc-600', colors: { from: '#71717A', to: '#52525B' } },
  { name: 'Stone', from: 'stone-500', to: 'stone-600', colors: { from: '#78716C', to: '#57534E' } },
  { name: 'Violet Foncé', from: 'violet-600', to: 'violet-700', colors: { from: '#7C3AED', to: '#6D28D9' } },
  { name: 'Rose Foncé', from: 'rose-500', to: 'rose-600', colors: { from: '#F43F5E', to: '#E11D48' } }
];

export const CMSManager = ({ siteSettings, categories, footerSections, cmsContent, onRefresh, user }: CMSManagerProps) => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Site Settings States
  const [settingDialog, setSettingDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState<any>(null);
  const [settingForm, setSettingForm] = useState({
    key: '',
    value: '',
    description: '',
    category: 'general',
    data_type: 'text'
  });

  // Category States
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    icon: 'Package',
    color_from: 'blue-500',
    color_to: 'blue-600',
    description: '',
    product_count: 0,
    sort_order: 0,
    active: true,
    translations: { fr: '', en: '', ln: '', sw: '' }
  });

  // CMS Content States
  const [contentDialog, setContentDialog] = useState(false);
  const [editingContent, setEditingContent] = useState<any>(null);
  const [editMode, setEditMode] = useState<'visual' | 'code'>('visual');
  const [contentForm, setContentForm] = useState({
    title: '',
    key: '',
    content_type: 'page',
    status: 'published',
    meta_title: '',
    meta_description: '',
    featured_image: '',
    content: {},
    language: 'fr'
  });
  const [contentHtml, setContentHtml] = useState('');

  // Footer States
  const [footerDialog, setFooterDialog] = useState(false);
  const [editingFooter, setEditingFooter] = useState<any>(null);
  const [footerForm, setFooterForm] = useState({
    title: '',
    section_key: '',
    content: [],
    sort_order: 0,
    active: true
  });

  // Menu States  
  const [menuDialog, setMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState<any>(null);
  const [menuForm, setMenuForm] = useState({
    title: '',
    url: '',
    icon: '',
    sort_order: 0,
    active: true,
    parent_id: null
  });
  const [menuItems, setMenuItems] = useState<any[]>([]);

  // Handlers for Site Settings
  const handleSettingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const settingData = {
        ...settingForm,
        value: JSON.stringify(settingForm.value)
      };

      if (editingSetting) {
        const { error } = await supabase
          .from('site_settings')
          .update(settingData)
          .eq('id', editingSetting.id);
        if (error) throw error;
        toast({ title: "Paramètre mis à jour avec succès" });
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert(settingData);
        if (error) throw error;
        toast({ title: "Paramètre créé avec succès" });
      }

      await onRefresh();
      setSettingDialog(false);
      resetSettingForm();
      setActiveTab('site-settings');
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const safeJsonParse = (value: any) => {
    if (!value) return '';
    try {
      if (typeof value === 'object') return value;
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  };

  const handleEditSetting = (setting: any) => {
    setSettingForm({
      key: setting.key,
      value: safeJsonParse(setting.value),
      description: setting.description || '',
      category: setting.category,
      data_type: setting.data_type
    });
    setEditingSetting(setting);
    setSettingDialog(true);
  };

  const resetSettingForm = () => {
    setSettingForm({
      key: '',
      value: '',
      description: '',
      category: 'general',
      data_type: 'text'
    });
    setEditingSetting(null);
  };

  // Handlers for Categories
  const translateCategoryName = async (categoryName: string): Promise<Record<string, string>> => {
    const translations: Record<string, string> = { fr: categoryName };
    const languages = ['en', 'ln', 'sw'];

    try {
      // Traduire dans chaque langue
      for (const lang of languages) {
        try {
          const { data, error } = await supabase.functions.invoke('translate-text', {
            body: {
              text: categoryName,
              targetLanguage: lang
            }
          });

          if (error) {
            console.error(`Translation error for ${lang}:`, error);
            translations[lang] = categoryName; // Fallback to original name
          } else if (data?.translatedTitle) {
            translations[lang] = data.translatedTitle;
          } else {
            translations[lang] = categoryName;
          }
        } catch (err) {
          console.error(`Translation error for ${lang}:`, err);
          translations[lang] = categoryName;
        }
      }
    } catch (error) {
      console.error('Translation process error:', error);
    }

    return translations;
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      toast({ 
        title: "Traduction en cours...",
        description: "Génération des traductions automatiques" 
      });

      // Utiliser les traductions déjà générées automatiquement OU les générer si manquantes
      const nameTranslations = categoryForm.translations || {
        fr: categoryForm.name,
        ...autoTranslateCategoryName(categoryForm.name)
      };

      const categoryData = {
        name: categoryForm.name,
        slug: categoryForm.slug,
        icon: categoryForm.icon,
        color_from: categoryForm.color_from,
        color_to: categoryForm.color_to,
        description: categoryForm.description,
        sort_order: categoryForm.sort_order,
        active: categoryForm.active,
        name_translations: nameTranslations,
        updated_by: user?.id
      };

      console.log('Updating category with data:', categoryData);
      console.log('Auto-translated to:', nameTranslations);
      console.log('editingCategory ID:', editingCategory?.id);

      if (editingCategory) {
        console.log('Executing UPDATE with id:', editingCategory.id);
        
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', editingCategory.id);
        
        if (error) throw error;
        
        // Vérifier que la mise à jour a bien eu lieu
        const { count } = await supabase
          .from('categories')
          .select('*', { count: 'exact', head: true })
          .eq('id', editingCategory.id);
        
        console.log('Update result:', { error, count });
        
        if (count === 0) {
          throw new Error('Aucune catégorie mise à jour - vérifier les permissions RLS');
        }
        
        toast({ 
          title: "Catégorie mise à jour avec succès",
          description: "Traductions générées automatiquement" 
        });
      } else {
        const { error } = await supabase
          .from('categories')
          .insert({ ...categoryData, created_by: user?.id });
        if (error) throw error;
        toast({ 
          title: "Catégorie créée avec succès",
          description: "Traductions générées automatiquement" 
        });
      }

      // Fermer le dialog et réinitialiser le formulaire AVANT le refresh
      setCategoryDialog(false);
      resetCategoryForm();
      setActiveTab('categories');
      // Refresh en arrière-plan
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditCategory = (category: any) => {
    // Charger les traductions existantes dans le formulaire
    const translations = category.name_translations || {
      fr: category.name,
      en: '',
      ln: '',
      sw: ''
    };
    
    setCategoryForm({
      ...category,
      translations: translations
    });
    setEditingCategory(category);
    setCategoryDialog(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm(t('admin.config.delete_confirm'))) return;
    
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);
      
      if (error) throw error;
      toast({ title: t('admin.config.category_deleted') });
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      slug: '',
      icon: 'Package',
      color_from: 'blue-500',
      color_to: 'blue-600',
      description: '',
      product_count: 0,
      sort_order: 0,
      active: true,
      translations: { fr: '', en: '', ln: '', sw: '' }
    });
    setEditingCategory(null);
    setIconSearchQuery(''); // Reset search
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleCategoryNameChange = (name: string) => {
    // Générer automatiquement les traductions basées sur le nom français
    const autoTranslations = autoTranslateCategoryName(name);
    
    setCategoryForm(prev => ({
      ...prev,
      name,
      slug: generateSlug(name),
      // Remplir automatiquement les traductions
      translations: {
        fr: name,
        en: autoTranslations.en,
        ln: autoTranslations.ln,
        sw: autoTranslations.sw,
      }
    }));
  };

  // CMS Content Handlers
  const handleContentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const contentData = {
        ...contentForm,
        content: JSON.stringify({
          ...contentForm.content,
          html: contentHtml
        })
      };

      if (editingContent) {
        const { error } = await supabase
          .from('cms_content')
          .update(contentData)
          .eq('id', editingContent.id);
        if (error) throw error;
        toast({ title: "Contenu mis à jour avec succès" });
      } else {
        // Vérifier si une page avec cette clé et langue existe déjà
        const { data: existing } = await supabase
          .from('cms_content')
          .select('id')
          .eq('key', contentData.key)
          .eq('language', contentData.language)
          .maybeSingle();

        if (existing) {
          toast({
            title: "Erreur",
            description: `Une page avec la clé "${contentData.key}" existe déjà pour la langue "${contentData.language}". Veuillez utiliser une clé différente.`,
            variant: "destructive"
          });
          return;
        }

        const { error } = await supabase
          .from('cms_content')
          .insert({ ...contentData, created_by: user?.id });
        if (error) throw error;
        toast({ title: "Contenu créé avec succès" });
      }

      await onRefresh();
      setContentDialog(false);
      resetContentForm();
      setActiveTab('content');
    } catch (error: any) {
      // Message d'erreur plus explicite pour la contrainte unique
      const errorMessage = error.message.includes('cms_content_key_language_unique') 
        ? `Une page avec cette clé existe déjà pour cette langue. Veuillez utiliser une clé unique.`
        : error.message;
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleEditContent = (content: any) => {
    const parsedContent = typeof content.content === 'string' ? JSON.parse(content.content || '{}') : content.content;
    setContentForm({
      title: content.title,
      key: content.key,
      content_type: content.content_type,
      status: content.status,
      meta_title: content.meta_title || '',
      meta_description: content.meta_description || '',
      featured_image: content.featured_image || '',
      content: parsedContent,
      language: content.language
    });
    setContentHtml(parsedContent.html || '');
    setEditingContent(content);
    setContentDialog(true);
  };

  const handleDeleteContent = async (contentId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) return;
    
    try {
      const { error } = await supabase
        .from('cms_content')
        .delete()
        .eq('id', contentId);
      
      if (error) throw error;
      toast({ title: "Contenu supprimé avec succès" });
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetContentForm = () => {
    setContentForm({
      title: '',
      key: '',
      content_type: 'page',
      status: 'published',
      meta_title: '',
      meta_description: '',
      featured_image: '',
      content: {},
      language: 'fr'
    });
    setContentHtml('');
    setEditingContent(null);
    setEditMode('visual');
  };

  // Footer Handlers
  const handleFooterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const footerData = {
        ...footerForm,
        content: JSON.stringify(footerForm.content)
      };

      if (editingFooter) {
        const { error } = await supabase
          .from('footer_sections')
          .update(footerData)
          .eq('id', editingFooter.id);
        if (error) throw error;
        toast({ title: t('cms.footer.updated_success') });
      } else {
        const { error } = await supabase
          .from('footer_sections')
          .insert(footerData);
        if (error) throw error;
        toast({ title: t('cms.footer.created_success') });
      }

      await onRefresh();
      setFooterDialog(false);
      resetFooterForm();
      setActiveTab('footer');
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditFooter = (footer: any) => {
    let parsedContent = [];
    try {
      if (typeof footer.content === 'string') {
        parsedContent = JSON.parse(footer.content || '[]');
      } else if (Array.isArray(footer.content)) {
        parsedContent = footer.content;
      }
    } catch (error) {
      console.error('Error parsing footer content:', error);
      parsedContent = [];
    }
    
    // Ensure parsedContent is always an array
    if (!Array.isArray(parsedContent)) {
      parsedContent = [];
    }
    
    setFooterForm({
      title: footer.title,
      section_key: footer.section_key,
      content: parsedContent,
      sort_order: footer.sort_order,
      active: footer.active
    });
    setEditingFooter(footer);
    setFooterDialog(true);
  };

  const handleDeleteFooter = async (footerId: string) => {
    if (!confirm(t('cms.footer.confirm_delete'))) return;
    
    try {
      const { error } = await supabase
        .from('footer_sections')
        .delete()
        .eq('id', footerId);
      
      if (error) throw error;
      toast({ title: t('cms.footer.deleted_success') });
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const resetFooterForm = () => {
    setFooterForm({
      title: '',
      section_key: '',
      content: [],
      sort_order: 0,
      active: true
    });
    setEditingFooter(null);
  };

  const addFooterLink = () => {
    setFooterForm(prev => ({
      ...prev,
      content: [...prev.content, { title: '', url: '', icon: '' }]
    }));
  };

  const updateFooterLink = (index: number, field: string, value: string) => {
    setFooterForm(prev => ({
      ...prev,
      content: prev.content.map((link: any, i: number) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeFooterLink = (index: number) => {
    setFooterForm(prev => ({
      ...prev,
      content: prev.content.filter((_: any, i: number) => i !== index)
    }));
  };

  const renderSettingControl = (setting: any) => {
    const value = safeJsonParse(setting.value);
    
    switch (setting.data_type) {
      case 'boolean':
        return (
          <Switch
            checked={value}
            onCheckedChange={(checked) => handleSettingChange(setting.id, checked)}
          />
        );
      case 'select':
        return (
          <Select value={value} onValueChange={(val) => handleSettingChange(setting.id, val)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Clair</SelectItem>
              <SelectItem value="dark">Sombre</SelectItem>
              <SelectItem value="auto">Automatique</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'color':
        return (
          <div className="flex items-center space-x-2">
            <div 
              className="w-8 h-8 rounded border"
              style={{ backgroundColor: value }}
            ></div>
            <Input
              type="color"
              value={value}
              onChange={(e) => handleSettingChange(setting.id, e.target.value)}
              className="w-20 h-8"
            />
          </div>
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleSettingChange(setting.id, e.target.value)}
            placeholder={setting.description}
          />
        );
    }
  };

  const handleSettingChange = async (settingId: string, value: any) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ 
          value: JSON.stringify(value),
          updated_at: new Date().toISOString()
        })
        .eq('id', settingId);
      
      if (error) throw error;
      
      toast({ 
        title: "Paramètre enregistré",
        description: "Les modifications ont été sauvegardées"
      });
      
      onRefresh();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const settingsByCategory = siteSettings.reduce((acc: any, setting: any) => {
    if (!acc[setting.category]) acc[setting.category] = [];
    acc[setting.category].push(setting);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 gap-1 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
          <TabsTrigger value="dashboard" className="flex items-center space-x-2 rounded-lg">
            <Layout className="h-4 w-4" />
            <span className="hidden sm:inline">{t('cms.tabs.dashboard')}</span>
          </TabsTrigger>
          <TabsTrigger value="hero" className="flex items-center space-x-2 rounded-lg">
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Hero</span>
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center space-x-2 rounded-lg">
            <Grid3x3 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('cms.tabs.categories')}</span>
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex items-center space-x-2 rounded-lg">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Pages</span>
          </TabsTrigger>
          <TabsTrigger value="menus" className="flex items-center space-x-2 rounded-lg">
            <Link className="h-4 w-4" />
            <span className="hidden sm:inline">{t('cms.tabs.menus')}</span>
          </TabsTrigger>
          <TabsTrigger value="footer" className="flex items-center space-x-2 rounded-lg">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">{t('cms.tabs.footer')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                    <Grid3x3 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-700">Catégories</p>
                    <p className="text-2xl font-bold text-green-900">{categories.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200/50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-700">Pages</p>
                    <p className="text-2xl font-bold text-purple-900">{cmsContent.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-200/50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-orange-500 rounded-xl shadow-lg">
                    <Palette className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-orange-700">Sections Footer</p>
                    <p className="text-2xl font-bold text-orange-900">{footerSections.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <span>{t('admin.quick_actions')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  type="button"
                  onClick={() => { 
                    resetCategoryForm(); 
                    setCategoryDialog(true); 
                  }}
                  className="w-full justify-start h-12 bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg"
                >
                  <Grid3x3 className="h-4 w-4 mr-3" />
                  {t('admin.new_category')}
                </Button>
                <Button 
                  onClick={() => { resetContentForm(); setContentDialog(true); }}
                  variant="outline"
                  className="w-full justify-start h-12 bg-white/50 backdrop-blur-sm"
                >
                  <FileText className="h-4 w-4 mr-3" />
                  {t('admin.new_page')}
                </Button>
                <Button 
                  onClick={() => { resetFooterForm(); setFooterDialog(true); }}
                  className="w-full justify-start h-12 bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg"
                >
                  <Palette className="h-4 w-4 mr-3" />
                  {t('admin.new_footer_section')}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <span>Statistiques</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t('admin.config.active_categories')}</span>
                  <Badge className="bg-green-100 text-green-800">
                    {categories.filter((c: any) => c.active).length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Pages publiées</span>
                  <Badge className="bg-blue-100 text-blue-800">
                    {cmsContent.filter((c: any) => c.status === 'published').length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Sections footer</span>
                  <Badge className="bg-purple-100 text-purple-800">
                    {footerSections.filter((f: any) => f.active).length}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Hero Image Tab */}
        <TabsContent value="hero" className="space-y-6">
          <HeroImageManager />
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <span>{t('admin.quick_actions')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  type="button"
                  onClick={() => { 
                    resetCategoryForm(); 
                    setCategoryDialog(true); 
                  }}
                  className="w-full justify-start bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl transition-all"
                >
                  <Grid3x3 className="h-4 w-4 mr-2" />
                  Nouvelle catégorie
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50 shadow-lg col-span-2">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                      <Grid3x3 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-700">{t('admin.config.active_categories')}</p>
                      <p className="text-2xl font-bold text-green-900">
                        {categories.filter((c: any) => c.active).length}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{t('admin.config.total_categories')}</p>
                    <p className="text-lg font-semibold">{categories.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Grid3x3 className="h-6 w-6 text-primary" />
                    <span>{t('admin.config.category_management')}</span>
                  </CardTitle>
                  <CardDescription>
                    {t('admin.config.category_management_desc')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {categories.map((category: any) => (
                  <div key={category.id} className="bg-gradient-to-r from-white to-gray-50/50 p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div 
                          className={`p-3 rounded-xl shadow-lg bg-gradient-to-r from-${category.color_from} to-${category.color_to}`}
                          style={{
                            background: `linear-gradient(135deg, ${colorGradients.find(g => g.from === category.color_from)?.colors.from || '#3B82F6'}, ${colorGradients.find(g => g.to === category.color_to)?.colors.to || '#2563EB'})`
                          }}
                        >
                          <div className="h-6 w-6 text-white">
                            {/* Dynamic icon rendering would go here */}
                            <Grid3x3 className="h-6 w-6" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw'] || category.name}</h3>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                          <div className="flex items-center space-x-2 mt-2">
                            <Badge variant={category.active ? "default" : "secondary"}>
                              {category.active ? t('admin.config.category_active') : t('admin.config.category_inactive')}
                            </Badge>
                            <Badge variant="outline">
                              {category.product_count} {t('admin.config.products')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditCategory(category)}
                          className="bg-white/80 hover:bg-white shadow-md"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                          className="bg-white/80 hover:bg-red-50 hover:text-red-600 shadow-md"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                   </div>
                 </div>
               ))}
              </div>
            </CardContent>
          </Card>

        </TabsContent>

        {/* Footer Tab */}
        <TabsContent value="footer" className="space-y-6">
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Palette className="h-6 w-6 text-primary" />
                    <span>{t('cms.footer.sections_title')}</span>
                  </CardTitle>
                  <CardDescription>
                    {t('cms.footer.sections_desc')}
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { resetFooterForm(); setFooterDialog(true); }}
                  className="bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('cms.footer.new_section')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {footerSections.length === 0 ? (
                <div className="text-center py-12">
                  <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('cms.footer.no_sections')}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('cms.footer.create_first_section')}
                  </p>
                  <Button 
                    onClick={() => { resetFooterForm(); setFooterDialog(true); }}
                    className="bg-gradient-to-r from-purple-500 to-purple-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('cms.footer.new_section')}
                  </Button>
                </div>
              ) : (
                <div className="grid gap-6">
                  {footerSections.map((section: any) => {
                    // Parse content safely
                    let contentArray = [];
                    try {
                      if (typeof section.content === 'string') {
                        contentArray = JSON.parse(section.content);
                      } else if (Array.isArray(section.content)) {
                        contentArray = section.content;
                      }
                    } catch (e) {
                      contentArray = [];
                    }

                    return (
                      <div key={section.id} className="bg-gradient-to-r from-white to-gray-50/50 p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2 flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="font-semibold text-lg">{section.title}</h3>
                              <Badge variant={section.active ? "default" : "secondary"}>
                                {section.active ? t('active') : t('inactive')}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {section.section_key}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {contentArray.length} {t('cms.footer.links')}
                            </p>
                            
                            {/* Show links preview */}
                            {contentArray.length > 0 && (
                              <div className="mt-3 space-y-1">
                                {contentArray.slice(0, 3).map((link: any, idx: number) => (
                                  <div key={idx} className="text-xs text-muted-foreground flex items-center space-x-2">
                                    <Link className="h-3 w-3" />
                                    <span>{link.title || link.label || t('cms.footer.untitled')}</span>
                                    <span className="text-primary/60">→ {link.url || '#'}</span>
                                  </div>
                                ))}
                                {contentArray.length > 3 && (
                                  <p className="text-xs text-muted-foreground italic">
                                    + {contentArray.length - 3} {t('cms.footer.more')}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              type="button"
                              className="min-w-[44px] min-h-[44px] p-2 bg-white hover:bg-blue-50 hover:text-blue-600 border-gray-300 shadow-sm transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('✅ Edit button clicked for section:', section.title);
                                handleEditFooter(section);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('✅ Edit button activated via keyboard for section:', section.title);
                                  handleEditFooter(section);
                                }
                              }}
                              aria-label={`${t('cms.footer.edit_section')}: ${section.title}`}
                              tabIndex={0}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              type="button"
                              className="min-w-[44px] min-h-[44px] p-2 bg-white hover:bg-red-50 hover:text-red-600 border-gray-300 shadow-sm transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('✅ Delete button clicked for section:', section.title);
                                handleDeleteFooter(section.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('✅ Delete button activated via keyboard for section:', section.title);
                                  handleDeleteFooter(section.id);
                                }
                              }}
                              aria-label={`${t('cms.footer.delete_section')}: ${section.title}`}
                              tabIndex={0}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
               </div>
             )}
           </CardContent>
         </Card>
        </TabsContent>

        {/* Pages Tab with nested tabs */}
        <TabsContent value="pages" className="space-y-6">
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-6 w-6 text-primary" />
                <span>Gestion des pages</span>
              </CardTitle>
              <CardDescription>
                Modifiez le contenu des pages de votre site
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="about" className="space-y-6">
                <TabsList className="grid w-full grid-cols-7 gap-1">
                  <TabsTrigger value="about">À Propos</TabsTrigger>
                  <TabsTrigger value="how-it-works">Comment ça marche</TabsTrigger>
                  <TabsTrigger value="support">Support</TabsTrigger>
                  <TabsTrigger value="become-vendor">Devenir vendeur</TabsTrigger>
                  <TabsTrigger value="terms">CGU</TabsTrigger>
                  <TabsTrigger value="privacy">Confidentialité</TabsTrigger>
                  <TabsTrigger value="legal">Mentions légales</TabsTrigger>
                </TabsList>

                <TabsContent value="about" className="space-y-6">
                  <PageEditor 
                    pageKey="about-page"
                    pageTitle="À Propos"
                    user={user}
                    onRefresh={onRefresh}
                    defaultContent={{
                      hero_title: "À Propos",
                      hero_description: "Votre marketplace de confiance",
                      mission_title: "Notre Mission",
                      mission_content: "<p>Votre contenu ici...</p>",
                      values_title: "Nos Valeurs",
                      values: [
                        {
                          icon: "shield",
                          title: "Sécurité",
                          description: "Protection maximale pour toutes vos transactions avec notre système de paiement sécurisé et vérification des produits."
                        },
                        {
                          icon: "users",
                          title: "Communauté",
                          description: "Une communauté active et solidaire qui favorise les échanges locaux et le commerce de proximité."
                        },
                        {
                          icon: "package",
                          title: "Fiabilité",
                          description: "Un réseau de points relais vérifiés et un service client disponible pour garantir votre satisfaction."
                        },
                        {
                          icon: "trending-up",
                          title: "Innovation",
                          description: "Des solutions technologiques modernes pour simplifier vos achats et ventes en ligne."
                        }
                      ],
                      stats: {
                        vendors: "1000+",
                        vendors_label: "Vendeurs Actifs",
                        customers: "10,000+",
                        customers_label: "Clients Satisfaits",
                        relays: "50+",
                        relays_label: "Points Relais"
                      },
                      faq: []
                    }}
                  />
                </TabsContent>

                <TabsContent value="how-it-works" className="space-y-6">
                  <PageEditor 
                    pageKey="how-it-works-page"
                    pageTitle="Comment ça marche"
                    user={user}
                    onRefresh={onRefresh}
                    defaultContent={{
                      hero_title: "Comment ça marche",
                      hero_description: "<p>Découvrez notre système innovant de points relais qui rend l'achat et la vente en ligne plus sûrs, plus simples et plus accessibles pour tous.</p>",
                      buyer_title: "Pour les Acheteurs",
                      buyer_content: "<div style='display: grid; gap: 2rem;'><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #3B82F6, #2563EB); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>1</div><div><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;'>Trouvez votre produit idéal</h3><p style='color: #64748b; line-height: 1.6;'>Parcourez des milliers de produits dans notre marketplace. Utilisez nos filtres avancés pour trouver exactement ce que vous cherchez : catégorie, prix, localisation, état du produit...</p></div></div><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #3B82F6, #2563EB); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>2</div><div><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;'>Sélectionnez un point relais</h3><p style='color: #64748b; line-height: 1.6;'>Choisissez parmi notre réseau de points relais sécurisés près de chez vous ou de votre lieu de travail. Consultez les horaires d'ouverture et les itinéraires pour plus de commodité.</p></div></div><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #3B82F6, #2563EB); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>3</div><div><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;'>Payez selon votre préférence</h3><p style='color: #64748b; line-height: 1.6;'>Payez en toute sécurité par carte bancaire en ligne via Stripe, ou choisissez le paiement en espèces directement au point relais lors de la récupération. Vous gardez le contrôle !</p></div></div><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #3B82F6, #2563EB); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>4</div><div><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;'>Récupérez en toute sécurité</h3><p style='color: #64748b; line-height: 1.6;'>Vous recevez un code QR unique par email et SMS. Présentez-le au point relais, vérifiez votre produit sur place et repartez avec votre achat. Simple et sécurisé !</p></div></div></div>",
                      seller_title: "Pour les Vendeurs",
                      seller_content: "<div style='display: grid; gap: 2rem; max-width: 900px; margin: 0 auto;'><div style='background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e2e8f0;'><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #10B981, #059669); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>1</div><div style='flex: 1;'><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; color: #1e293b;'>Créez votre boutique en 5 minutes</h3><p style='color: #64748b; line-height: 1.6; margin-bottom: 1rem;'>Inscrivez-vous gratuitement, ajoutez vos produits avec photos et descriptions détaillées. Définissez vos prix et votre zone de livraison. C'est tout !</p><p style='color: #3B82F6; font-weight: 500;'>✓ Aucun frais d'inscription • ✓ Commission de seulement 8%</p></div></div></div><div style='background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e2e8f0;'><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #10B981, #059669); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>2</div><div style='flex: 1;'><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; color: #1e293b;'>Déposez au point relais</h3><p style='color: #64748b; line-height: 1.6; margin-bottom: 1rem;'>Dès qu'un client achète, vous recevez une notification avec l'adresse du point relais choisi. Déposez le produit emballé avec l'étiquette fournie. Le point relais le garde en sécurité.</p><p style='color: #10B981; font-weight: 500;'>✓ Pas de frais de livraison à gérer • ✓ Horaires flexibles</p></div></div></div><div style='background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e2e8f0;'><div style='display: flex; gap: 1rem; align-items: start;'><div style='flex-shrink: 0; width: 60px; height: 60px; background: linear-gradient(135deg, #10B981, #059669); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;'>3</div><div style='flex: 1;'><h3 style='font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; color: #1e293b;'>Recevez votre paiement rapidement</h3><p style='color: #64748b; line-height: 1.6; margin-bottom: 1rem;'>Une fois que l'acheteur récupère et confirme la commande, vous êtes payé automatiquement sous 24h sur votre compte bancaire ou mobile money. Aucune démarche supplémentaire !</p><p style='color: #F59E0B; font-weight: 500;'>✓ Paiement sous 24h garanti • ✓ Protection contre les litiges</p></div></div></div></div>",
                      benefits_title: "Pourquoi choisir Nazandoo ?",
                      benefits_content: "<div style='display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem;'><div style='display: flex; align-items: start; gap: 1rem;'><div style='flex-shrink: 0; width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;'><svg width='24' height='24' fill='currentColor' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clip-rule='evenodd'/></svg></div><div><h3 style='font-weight: 600; font-size: 1.125rem; margin-bottom: 0.5rem;'>Sécurité Garantie</h3><p style='opacity: 0.9; line-height: 1.6;'>Vérifiez votre produit au point relais avant le paiement final. Protection acheteur et vendeur. Système de litiges intégré pour votre tranquillité.</p></div></div><div style='display: flex; align-items: start; gap: 1rem;'><div style='flex-shrink: 0; width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;'><svg width='24' height='24' fill='currentColor' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z' clip-rule='evenodd'/></svg></div><div><h3 style='font-weight: 600; font-size: 1.125rem; margin-bottom: 0.5rem;'>Flexibilité de Paiement</h3><p style='opacity: 0.9; line-height: 1.6;'>Carte bancaire en ligne ou espèces au point relais. Mobile money bientôt disponible. Vous choisissez ce qui vous convient le mieux.</p></div></div><div style='display: flex; align-items: start; gap: 1rem;'><div style='flex-shrink: 0; width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;'><svg width='24' height='24' fill='currentColor' viewBox='0 0 20 20'><path d='M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z'/><path d='M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z'/></svg></div><div><h3 style='font-weight: 600; font-size: 1.125rem; margin-bottom: 0.5rem;'>Support Client Réactif</h3><p style='opacity: 0.9; line-height: 1.6;'>Équipe support disponible par chat, email et téléphone du lundi au samedi. Réponse sous 2h en moyenne. Assistance en français et lingala.</p></div></div><div style='display: flex; align-items: start; gap: 1rem;'><div style='flex-shrink: 0; width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;'><svg width='24' height='24' fill='currentColor' viewBox='0 0 20 20'><path fill-rule='evenodd' d='M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z' clip-rule='evenodd'/></svg></div><div><h3 style='font-weight: 600; font-size: 1.125rem; margin-bottom: 0.5rem;'>Commerce Local</h3><p style='opacity: 0.9; line-height: 1.6;'>Soutenez l'économie congolaise en achetant local. Rencontrez des vendeurs de votre quartier. Réduisez l'empreinte carbone en évitant les livraisons longue distance.</p></div></div></div>",
                      faq: [
                        {
                          question: "Comment fonctionne le système de points relais ?",
                          answer: "<p>Notre système de points relais est simple et sécurisé :</p><ol><li><strong>Choix du point relais</strong> : Lors de votre achat, vous sélectionnez le point relais le plus proche de chez vous parmi notre réseau de partenaires de confiance (épiceries, boutiques, stations-service).</li><li><strong>Dépôt par le vendeur</strong> : Le vendeur dépose le produit emballé au point relais dans les 48h suivant votre commande.</li><li><strong>Notification</strong> : Vous recevez un SMS et un email avec un code QR unique dès que votre colis est disponible.</li><li><strong>Récupération</strong> : Présentez votre code QR au point relais, vérifiez le produit sur place, et confirmez la réception. C'est tout !</li></ol><p>Le point relais garde votre colis en sécurité pendant 7 jours. Passé ce délai, il est retourné au vendeur et vous êtes automatiquement remboursé.</p>"
                        },
                        {
                          question: "Quels sont les modes de paiement acceptés ?",
                          answer: "<p>Nous offrons plusieurs options de paiement pour votre confort :</p><ul><li><strong>Paiement en ligne par carte bancaire</strong> : Visa, Mastercard via notre plateforme sécurisée Stripe. Vos données bancaires sont cryptées et jamais stockées sur nos serveurs.</li><li><strong>Paiement en espèces au point relais</strong> : Payez directement en espèces (francs congolais ou dollars) lorsque vous récupérez votre commande. Aucun frais supplémentaire.</li><li><strong>Mobile Money</strong> (bientôt disponible) : M-Pesa, Airtel Money, Orange Money - paiement instantané depuis votre téléphone.</li></ul><p><strong>Important</strong> : Si vous choisissez le paiement en espèces, le vendeur ne reçoit son argent qu'après confirmation de votre part au point relais. Votre sécurité est notre priorité !</p>"
                        },
                        {
                          question: "Combien de temps ai-je pour récupérer ma commande ?",
                          answer: "<p>Vous disposez de <strong>7 jours calendaires</strong> pour récupérer votre commande au point relais à partir de la date de notification.</p><p><strong>Que se passe-t-il si je ne récupère pas à temps ?</strong></p><ul><li>Le colis est automatiquement retourné au vendeur</li><li>Vous êtes <strong>remboursé intégralement</strong> sous 48h (y compris les frais de service)</li><li>Aucun frais de pénalité n'est appliqué</li></ul><p><strong>Conseils</strong> :</p><ul><li>Vérifiez les horaires d'ouverture de votre point relais avant de commander</li><li>Activez les notifications pour recevoir des rappels</li><li>Si vous avez un empêchement, contactez notre support pour prolonger le délai (possible une seule fois, +3 jours)</li></ul>"
                        },
                        {
                          question: "Puis-je retourner un produit ?",
                          answer: "<p>Oui ! Vous bénéficiez d'un <strong>droit de rétractation de 14 jours</strong> conformément à la législation en vigueur.</p><p><strong>Conditions de retour</strong> :</p><ul><li>Le produit doit être dans son état d'origine, non utilisé et avec son emballage</li><li>Les étiquettes et accessoires doivent être présents</li><li>Certains produits sont exclus : denrées périssables, produits d'hygiène personnelle ouverts, articles sur mesure</li></ul><p><strong>Procédure de retour</strong> :</p><ol><li>Connectez-vous à votre compte et accédez à \"Mes Commandes\"</li><li>Sélectionnez la commande et cliquez sur \"Demander un retour\"</li><li>Indiquez le motif du retour (conforme/non conforme à la description, défectueux, etc.)</li><li>Déposez le produit au même point relais dans les 48h</li><li>Le vendeur vérifie l'état du produit</li><li>Vous êtes remboursé sous 3 à 5 jours ouvrables</li></ol><p><strong>Produit défectueux ou non conforme ?</strong> Ouvrez un litige depuis votre tableau de bord. Notre équipe médie entre vous et le vendeur. Les frais de retour sont à la charge du vendeur dans ce cas.</p>"
                        },
                        {
                          question: "Comment devenir vendeur sur Nazandoo ?",
                          answer: "<p>Rejoindre Nazandoo en tant que vendeur est simple et rapide :</p><p><strong>Étape 1 : Inscription (5 minutes)</strong></p><ul><li>Cliquez sur \"Devenir Vendeur\" dans le menu principal</li><li>Remplissez le formulaire avec vos informations : nom, email, téléphone, adresse</li><li>Choisissez votre type de compte : Particulier ou Professionnel (entreprise)</li></ul><p><strong>Étape 2 : Vérification (24-48h)</strong></p><ul><li>Téléchargez une pièce d'identité (carte d'identité, passeport ou permis de conduire)</li><li>Pour les professionnels : ajoutez votre RCCM ou Numéro Impôt</li><li>Notre équipe vérifie vos documents sous 48h max</li><li>Vous recevez un email de confirmation une fois approuvé</li></ul><p><strong>Étape 3 : Commencez à vendre</strong></p><ul><li>Ajoutez vos produits avec photos, descriptions et prix</li><li>Définissez vos zones de livraison (quartiers, communes)</li><li>Publiez et commencez à recevoir des commandes !</li></ul><p><strong>Avantages vendeur</strong> :</p><ul><li>Aucun frais d'inscription ou d'abonnement mensuel</li><li>Commission de seulement <strong>8%</strong> sur chaque vente (une des plus basses du marché !)</li><li>Paiement garanti sous 24h après confirmation de livraison</li><li>Tableau de bord complet avec statistiques de ventes</li><li>Support dédié pour les vendeurs</li></ul>"
                        },
                        {
                          question: "Comment puis-je suivre ma commande ?",
                          answer: "<p>Le suivi de votre commande est simple et transparent à chaque étape :</p><p><strong>Depuis votre compte</strong> :</p><ol><li>Connectez-vous sur <strong>nazandoo.com</strong></li><li>Accédez à la section <strong>\"Mes Commandes\"</strong> dans votre tableau de bord</li><li>Cliquez sur la commande que vous souhaitez suivre</li></ol><p><strong>Statuts de commande</strong> :</p><ul><li>🔵 <strong>En attente de paiement</strong> : Commande créée, en attente de votre paiement (15 min max)</li><li>🟡 <strong>Payée - En préparation</strong> : Paiement confirmé, vendeur prépare le colis (48h max)</li><li>🟠 <strong>En transit vers le point relais</strong> : Le vendeur a déposé le colis au point relais</li><li>🟢 <strong>Disponible au point relais</strong> : Votre colis vous attend ! Code QR envoyé par SMS et email</li><li>✅ <strong>Livrée</strong> : Vous avez récupéré et confirmé la réception</li><li>❌ <strong>Annulée/Retournée</strong> : Commande annulée ou non récupérée dans les délais</li></ul><p><strong>Notifications en temps réel</strong> :</p><ul><li>Email à chaque changement de statut</li><li>SMS pour les étapes importantes (paiement, disponibilité au point relais)</li><li>Notifications push sur l'application mobile (si installée)</li></ul><p><strong>Besoin d'aide ?</strong> Contactez le vendeur directement via la messagerie intégrée ou notre support client si nécessaire.</p>"
                        },
                        {
                          question: "Que faire si j'ai un problème avec ma commande ?",
                          answer: "<p>Si vous rencontrez un problème, nous sommes là pour vous aider :</p><p><strong>1. Contactez d'abord le vendeur</strong></p><ul><li>Accédez à votre commande dans \"Mes Commandes\"</li><li>Utilisez la messagerie intégrée pour discuter avec le vendeur</li><li>La plupart des problèmes se règlent rapidement par dialogue direct</li></ul><p><strong>2. Ouvrez un litige (si nécessaire)</strong></p><p>Si le vendeur ne répond pas sous 48h ou si vous n'êtes pas satisfait de sa réponse :</p><ul><li>Cliquez sur <strong>\"Ouvrir un litige\"</strong> depuis votre commande</li><li>Décrivez le problème et joignez des photos si pertinent</li><li>Notre équipe de médiation intervient sous 24h</li></ul><p><strong>Types de litiges courants</strong> :</p><ul><li><strong>Produit non conforme</strong> : différent de la description ou des photos</li><li><strong>Produit défectueux</strong> : ne fonctionne pas ou endommagé</li><li><strong>Article manquant</strong> : colis incomplet</li><li><strong>Problème de qualité</strong> : état inférieur à celui annoncé</li></ul><p><strong>Solutions possibles</strong> :</p><ul><li>Remboursement partiel ou total</li><li>Remplacement du produit</li><li>Retour avec frais à la charge du vendeur</li><li>Crédit sur votre compte Nazandoo</li></ul><p><strong>Garantie Nazandoo</strong> : Si un vendeur ne respecte pas ses engagements, nous vous remboursons intégralement depuis notre fonds de garantie. Votre satisfaction est notre priorité !</p>"
                        },
                        {
                          question: "Y a-t-il des frais supplémentaires ?",
                          answer: "<p>Chez Nazandoo, nous croyons en la <strong>transparence totale</strong> des prix :</p><p><strong>Pour les acheteurs</strong> :</p><ul><li>✅ <strong>Aucun frais de livraison</strong> : Le système de points relais est gratuit</li><li>✅ <strong>Aucun frais de service</strong> pour l'utilisation de la plateforme</li><li>✅ <strong>Aucun frais de paiement</strong> en espèces au point relais</li><li>⚠️ <strong>Frais de transaction bancaire</strong> : 2.5% + 50 FC si vous payez par carte bancaire en ligne (frais standard Stripe, non majorés par nous)</li></ul><p><strong>Pour les vendeurs</strong> :</p><ul><li>✅ <strong>Aucun frais d'inscription</strong> ou abonnement mensuel</li><li>✅ <strong>Aucun frais de listing</strong> de produits (illimité)</li><li>⚠️ <strong>Commission de 8%</strong> uniquement sur les ventes réalisées</li><li>✅ <strong>Paiements inclus</strong> : Nous gérons tous les paiements et virements</li></ul><p><strong>Exemple de calcul vendeur</strong> :</p><p>Vous vendez un article à 10 000 FC :</p><ul><li>Prix affiché à l'acheteur : <strong>10 000 FC</strong></li><li>Commission Nazandoo (8%) : <strong>800 FC</strong></li><li>Vous recevez : <strong>9 200 FC</strong></li></ul><p>Simple, clair et équitable pour tous ! 💙</p>"
                        }
                      ]
                    }}
                  />
                </TabsContent>

                <TabsContent value="support" className="space-y-6">
                  <PageEditor 
                    pageKey="support-page"
                    pageTitle="Support"
                    user={user}
                    onRefresh={onRefresh}
                    defaultContent={{
                      hero_title: "Support",
                      hero_description: "<p>Nous sommes là pour vous aider</p>",
                      contact_title: "Contactez-nous",
                      email_title: "Email",
                      email_description: "<p>Envoyez-nous un email pour toute question</p>",
                      phone_title: "Téléphone",
                      phone_description: "<p>Appelez-nous pour un support immédiat</p>",
                      chat_title: "Chat en direct",
                      chat_description: "<p>Discutez avec notre équipe de support</p>",
                      chat_button: "Ouvrir le chat",
                      form_title: "Envoyez-nous un message",
                      faq_title: "Questions Fréquentes",
                      hours_title: "Horaires de Support",
                      hours_content: "<p>Lundi - Vendredi : 9h00 - 18h00</p><p>Samedi : 10h00 - 16h00</p><p>Dimanche : Fermé</p>",
                      faq: [
                        {
                          question: "Comment fonctionne le système de points relais ?",
                          answer: "<p>Les points relais sont des lieux sécurisés où vous pouvez récupérer vos commandes. Après votre achat, le vendeur dépose le produit au point relais choisi. Vous recevez un code QR que vous présentez au point relais pour récupérer votre commande.</p>"
                        },
                        {
                          question: "Quels sont les modes de paiement acceptés ?",
                          answer: "<p>Nous acceptons les paiements par carte bancaire en ligne (Visa, Mastercard) via Stripe, ainsi que le paiement en espèces directement au point relais lors de la récupération.</p>"
                        },
                        {
                          question: "Combien de temps ai-je pour récupérer ma commande ?",
                          answer: "<p>Vous disposez généralement de 7 jours pour récupérer votre commande au point relais. Passé ce délai, la commande peut être retournée au vendeur et vous serez remboursé.</p>"
                        },
                        {
                          question: "Puis-je retourner un produit ?",
                          answer: "<p>Oui, vous disposez d'un droit de rétractation de 14 jours. Si le produit ne correspond pas à la description ou présente un défaut, vous pouvez ouvrir un litige et demander un remboursement ou un échange.</p>"
                        },
                        {
                          question: "Comment devenir vendeur sur la plateforme ?",
                          answer: "<p>Cliquez sur \"Devenir Vendeur\" dans le menu principal, remplissez le formulaire d'inscription avec vos informations commerciales. Une fois votre compte approuvé par notre équipe, vous pourrez commencer à vendre.</p>"
                        },
                        {
                          question: "Comment puis-je suivre ma commande ?",
                          answer: "<p>Connectez-vous à votre compte et accédez à la section \"Mes Commandes\". Vous pourrez y voir le statut en temps réel de toutes vos commandes, du paiement à la disponibilité au point relais.</p>"
                        }
                      ]
                    }}
                  />
                </TabsContent>

                <TabsContent value="become-vendor" className="space-y-6">
                  <BecomeVendorEditor user={user} onRefresh={onRefresh} />
                </TabsContent>

                <TabsContent value="terms" className="space-y-6">
                  <LegalPageEditor 
                    slug="terms" 
                    pageTitle="Conditions Générales d'Utilisation (CGU)"
                  />
                </TabsContent>

                <TabsContent value="privacy" className="space-y-6">
                  <LegalPageEditor 
                    slug="privacy" 
                    pageTitle="Politique de Confidentialité"
                  />
                </TabsContent>

                <TabsContent value="legal" className="space-y-6">
                  <LegalPageEditor 
                    slug="terms" 
                    pageTitle="Mentions Légales"
                  />
                </TabsContent>

                <TabsContent value="legal-old" className="space-y-6" style={{display: 'none'}}>
                  <PageEditor 
                    pageKey="mentions_légales"
                    pageTitle="Mentions Légales (Old)"
                    user={user}
                    onRefresh={onRefresh}
                    defaultContent={{
                      hero_title: "Mentions Légales",
                      hero_description: "Informations légales et réglementaires concernant la plateforme Nazandoo",
                      content: {
                        html: `<h2>1. Informations sur l'Éditeur</h2>
<p><strong>Nom de la plateforme :</strong> Nazandoo<br/>
<strong>Raison sociale :</strong> Nazandoo SARL<br/>
<strong>Siège social :</strong> Kinshasa, République Démocratique du Congo<br/>
<strong>Email :</strong> contact@nazandoo.com<br/>
<strong>Téléphone :</strong> +243 XXX XXX XXX</p>

<p><strong>Directeur de la publication :</strong> [Nom du Directeur]<br/>
<strong>Numéro d'immatriculation :</strong> [RCCM ou équivalent]<br/>
<strong>Numéro d'identification fiscale :</strong> [Numéro fiscal]</p>

<h2>2. Hébergement de la Plateforme</h2>
<p>La plateforme Nazandoo est hébergée par :<br/>
<strong>Hébergeur :</strong> Supabase Inc.<br/>
<strong>Adresse :</strong> 970 Toa Payoh North, #07-04, Singapore 318992<br/>
<strong>Site web :</strong> <a href="https://supabase.com" target="_blank" rel="noopener">https://supabase.com</a></p>

<p><strong>Infrastructure cloud :</strong> AWS (Amazon Web Services)<br/>
<strong>Région de stockage des données :</strong> Europe (pour les utilisateurs africains)</p>

<h2>3. Description des Services</h2>
<p>Nazandoo est une plateforme de marketplace e-commerce qui met en relation des vendeurs et des acheteurs en République Démocratique du Congo et dans la région. Nos services incluent :</p>

<ul>
<li><strong>Marketplace :</strong> Mise en relation entre vendeurs professionnels et acheteurs</li>
<li><strong>Système de paiement :</strong> Facilitation des transactions via cartes bancaires et Mobile Money</li>
<li><strong>Points relais :</strong> Réseau de points de collecte pour retrait des commandes</li>
<li><strong>Gestion des commandes :</strong> Suivi en temps réel des commandes et livraisons</li>
<li><strong>Support client :</strong> Service d'assistance pour acheteurs et vendeurs</li>
</ul>

<h2>4. Conditions d'Utilisation</h2>
<p>L'utilisation de la plateforme Nazandoo implique l'acceptation pleine et entière des conditions générales d'utilisation disponibles sur notre site. Les utilisateurs s'engagent à :</p>

<ul>
<li>Fournir des informations exactes et à jour lors de leur inscription</li>
<li>Maintenir la confidentialité de leurs identifiants de connexion</li>
<li>Respecter les lois et réglementations en vigueur</li>
<li>Ne pas utiliser la plateforme à des fins frauduleuses ou illégales</li>
<li>Respecter les droits de propriété intellectuelle</li>
</ul>

<h2>5. Propriété Intellectuelle</h2>
<p>L'ensemble du contenu présent sur la plateforme Nazandoo (textes, images, logos, vidéos, design, base de données) est protégé par les lois relatives à la propriété intellectuelle.</p>

<p><strong>Droits d'auteur :</strong> Le design, la structure et le code source de la plateforme sont la propriété exclusive de Nazandoo SARL.</p>

<p><strong>Marques :</strong> Le nom "Nazandoo" et le logo associé sont des marques déposées. Toute reproduction non autorisée est interdite.</p>

<p><strong>Contenus des vendeurs :</strong> Les vendeurs conservent leurs droits sur les contenus qu'ils publient (descriptions, photos de produits). Ils accordent à Nazandoo une licence d'utilisation pour afficher ces contenus sur la plateforme.</p>

<p><strong>Contenus utilisateurs :</strong> Les avis et commentaires publiés par les utilisateurs restent leur propriété, mais ils accordent à Nazandoo le droit de les afficher publiquement.</p>

<h2>6. Protection des Données Personnelles</h2>
<p>Conformément aux réglementations sur la protection des données, Nazandoo s'engage à protéger la vie privée de ses utilisateurs. Pour plus d'informations sur la collecte, l'utilisation et la protection de vos données personnelles, veuillez consulter notre <a href="/privacy">Politique de Confidentialité</a>.</p>

<p><strong>Responsable du traitement :</strong> Nazandoo SARL<br/>
<strong>Délégué à la protection des données :</strong> privacy@nazandoo.com</p>

<h2>7. Cookies et Technologies de Suivi</h2>
<p>La plateforme Nazandoo utilise des cookies pour améliorer l'expérience utilisateur et analyser le trafic. Les utilisateurs peuvent gérer leurs préférences de cookies via les paramètres de leur navigateur.</p>

<p><strong>Types de cookies utilisés :</strong></p>
<ul>
<li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement de la plateforme</li>
<li><strong>Cookies de performance :</strong> Pour analyser l'utilisation du site</li>
<li><strong>Cookies de fonctionnalité :</strong> Pour mémoriser vos préférences</li>
</ul>

<h2>8. Responsabilité et Garanties</h2>
<p><strong>Rôle de Nazandoo :</strong> Nazandoo agit en tant qu'intermédiaire technique entre vendeurs et acheteurs. Nous ne sommes pas responsables de :</p>

<ul>
<li>La qualité, la conformité ou la légalité des produits vendus</li>
<li>La capacité des vendeurs à honorer leurs engagements</li>
<li>Les litiges entre acheteurs et vendeurs</li>
<li>Les interruptions de service dues à des problèmes techniques</li>
</ul>

<p><strong>Garanties :</strong> Cependant, nous nous efforçons de :</p>
<ul>
<li>Vérifier l'identité des vendeurs professionnels</li>
<li>Modérer les contenus publiés sur la plateforme</li>
<li>Fournir un système de résolution des litiges</li>
<li>Maintenir la sécurité et la disponibilité de la plateforme</li>
</ul>

<h2>9. Limitation de Responsabilité</h2>
<p>Nazandoo ne peut être tenu responsable des dommages directs ou indirects résultant de :</p>

<ul>
<li>L'utilisation ou l'impossibilité d'utiliser la plateforme</li>
<li>Les erreurs ou omissions dans les contenus publiés</li>
<li>Les interruptions ou dysfonctionnements techniques</li>
<li>Les pertes de données ou atteintes à la sécurité</li>
<li>Les actes frauduleux d'utilisateurs tiers</li>
</ul>

<p>En tout état de cause, la responsabilité de Nazandoo est limitée au montant des commissions perçues sur la transaction concernée.</p>

<h2>10. Liens Externes</h2>
<p>La plateforme peut contenir des liens vers des sites web externes. Nazandoo n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu, leur disponibilité ou leurs pratiques en matière de protection des données.</p>

<h2>11. Modification des Mentions Légales</h2>
<p>Nazandoo se réserve le droit de modifier les présentes mentions légales à tout moment. Les modifications entrent en vigueur dès leur publication sur la plateforme. Il est recommandé aux utilisateurs de consulter régulièrement cette page.</p>

<p><strong>Dernière mise à jour :</strong> Décembre 2025</p>

<h2>12. Droit Applicable et Juridiction</h2>
<p>Les présentes mentions légales sont régies par le droit congolais. En cas de litige, les parties s'efforceront de trouver une solution amiable. À défaut, les tribunaux de Kinshasa seront seuls compétents.</p>

<h2>13. Médiation et Résolution des Litiges</h2>
<p>Conformément aux dispositions légales en matière de médiation à la consommation, les utilisateurs peuvent recourir à une médiation en cas de litige :</p>

<p><strong>Service de médiation :</strong> [Nom du médiateur ou organisme]<br/>
<strong>Adresse :</strong> [Adresse du médiateur]<br/>
<strong>Email :</strong> mediation@nazandoo.com<br/>
<strong>Site web :</strong> [URL plateforme de médiation]</p>

<h2>14. Contact</h2>
<p>Pour toute question concernant ces mentions légales ou la plateforme Nazandoo, vous pouvez nous contacter :</p>

<p><strong>Email général :</strong> contact@nazandoo.com<br/>
<strong>Support client :</strong> support@nazandoo.com<br/>
<strong>Questions légales :</strong> legal@nazandoo.com<br/>
<strong>Protection des données :</strong> privacy@nazandoo.com</p>

<p><strong>Adresse postale :</strong><br/>
Nazandoo SARL<br/>
[Adresse complète]<br/>
Kinshasa, République Démocratique du Congo</p>

<p><strong>Téléphone :</strong> +243 XXX XXX XXX<br/>
<strong>Horaires :</strong> Lundi - Vendredi, 8h00 - 18h00 (heure locale)</p>`
                      }
                    }}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Menus Tab */}
        <TabsContent value="menus" className="space-y-6">
         <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
           <CardHeader>
             <div className="flex items-center justify-between">
               <div>
                 <CardTitle className="flex items-center space-x-2">
                   <Link className="h-6 w-6 text-primary" />
                   <span>Gestion des menus</span>
                 </CardTitle>
                 <CardDescription>
                   Configurez les menus de navigation et leurs couleurs
                 </CardDescription>
               </div>
               <Button 
                 onClick={() => { resetCategoryForm(); setCategoryDialog(true); }}
                 className="bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
               >
                 <Plus className="h-4 w-4 mr-2" />
                 Nouveau menu
               </Button>
             </div>
           </CardHeader>
           <CardContent>
             <div className="grid gap-6">
               {categories.map((category: any) => (
                 <div key={category.id} className="bg-gradient-to-r from-white to-gray-50/50 p-6 border rounded-xl shadow-lg hover:shadow-xl transition-all">
                   <div className="flex items-center justify-between">
                     <div className="flex items-center space-x-4">
                       <div 
                         className={`p-3 rounded-xl shadow-lg`}
                         style={{
                           background: `linear-gradient(135deg, ${colorGradients.find(g => g.from === category.color_from)?.colors.from || '#3B82F6'}, ${colorGradients.find(g => g.to === category.color_to)?.colors.to || '#2563EB'})`
                         }}
                       >
                         <div className="h-6 w-6 text-white">
                           <Grid3x3 className="h-6 w-6" />
                         </div>
                       </div>
                       <div className="flex-1">
                         <h3 className="font-semibold text-lg">{category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw'] || category.name}</h3>
                         <p className="text-sm text-muted-foreground">{category.description}</p>
                         <div className="flex items-center space-x-2 mt-2">
                           <Badge variant={category.active ? "default" : "secondary"}>
                             {category.active ? t('admin.config.category_active') : t('admin.config.category_inactive')}
                           </Badge>
                           <div className="flex items-center space-x-1">
                             <div className="w-4 h-4 rounded" style={{ backgroundColor: colorGradients.find(g => g.from === category.color_from)?.colors.from }}></div>
                             <span className="text-xs text-muted-foreground">→</span>
                             <div className="w-4 h-4 rounded" style={{ backgroundColor: colorGradients.find(g => g.to === category.color_to)?.colors.to }}></div>
                           </div>
                         </div>
                       </div>
                     </div>
                     <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }}
                          className="bg-white/80 hover:bg-white shadow-md relative z-10"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                          className="bg-white/80 hover:bg-red-50 hover:text-red-600 shadow-md relative z-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
       </TabsContent>
       </Tabs>

       {/* Category Dialog - Moved outside Tabs to fix display issue */}
       <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
         <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background">
           <DialogHeader>
             <DialogTitle>
               {editingCategory ? t('admin.config.edit_category') : t('admin.new_category')}
             </DialogTitle>
             <DialogDescription>
               {t('admin.config.category_management_desc')}
             </DialogDescription>
           </DialogHeader>
           <form onSubmit={handleCategorySubmit} className="space-y-6">
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="category_name">Nom de la catégorie</Label>
                 <Input
                   id="category_name"
                   value={categoryForm.name}
                   onChange={(e) => handleCategoryNameChange(e.target.value)}
                   required
                 />
               </div>
               <div>
                 <Label htmlFor="category_slug">Slug (URL)</Label>
                 <Input
                   id="category_slug"
                   value={categoryForm.slug}
                   onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value }))}
                   placeholder="electronique"
                   required
                 />
               </div>
             </div>

             <div>
               <Label htmlFor="category_description">Description</Label>
               <Textarea
                 id="category_description"
                 value={categoryForm.description}
                 onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
               />
             </div>

             {/* Afficher les traductions automatiques générées */}
             {categoryForm.translations && categoryForm.name && (
               <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 p-4 rounded-lg space-y-3 border border-blue-200 dark:border-blue-800">
                 <div className="flex items-center gap-2">
                   <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                   <Label className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                     Traductions automatiques (modifiables)
                   </Label>
                 </div>
                 <div className="grid grid-cols-2 gap-3">
                   <div className="space-y-1">
                     <div className="flex items-center gap-2">
                       <Badge variant="outline" className="bg-white dark:bg-gray-900">FR</Badge>
                       <span className="text-xs text-muted-foreground">Français</span>
                     </div>
                     <Input
                       value={categoryForm.translations.fr || categoryForm.name}
                       onChange={(e) => setCategoryForm(prev => ({
                         ...prev,
                         translations: { ...prev.translations, fr: e.target.value }
                       }))}
                       className="text-sm"
                     />
                   </div>
                   <div className="space-y-1">
                     <div className="flex items-center gap-2">
                       <Badge variant="outline" className="bg-white dark:bg-gray-900">EN</Badge>
                       <span className="text-xs text-muted-foreground">English</span>
                     </div>
                     <Input
                       value={categoryForm.translations.en}
                       onChange={(e) => setCategoryForm(prev => ({
                         ...prev,
                         translations: { ...prev.translations, en: e.target.value }
                       }))}
                       className="text-sm"
                     />
                   </div>
                   <div className="space-y-1">
                     <div className="flex items-center gap-2">
                       <Badge variant="outline" className="bg-white dark:bg-gray-900">LN</Badge>
                       <span className="text-xs text-muted-foreground">Lingala</span>
                     </div>
                     <Input
                       value={categoryForm.translations.ln}
                       onChange={(e) => setCategoryForm(prev => ({
                         ...prev,
                         translations: { ...prev.translations, ln: e.target.value }
                       }))}
                       className="text-sm"
                     />
                   </div>
                   <div className="space-y-1">
                     <div className="flex items-center gap-2">
                       <Badge variant="outline" className="bg-white dark:bg-gray-900">SW</Badge>
                       <span className="text-xs text-muted-foreground">Swahili</span>
                     </div>
                     <Input
                       value={categoryForm.translations.sw}
                       onChange={(e) => setCategoryForm(prev => ({
                         ...prev,
                         translations: { ...prev.translations, sw: e.target.value }
                       }))}
                       className="text-sm"
                     />
                   </div>
                 </div>
                 <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
                   <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0" />
                   <span>Les traductions sont générées automatiquement quand tu tapes le nom français. Tu peux les modifier avant de sauvegarder !</span>
                 </p>
               </div>
             )}

             {editingCategory && editingCategory.name_translations && (
               <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                 <Label className="text-sm font-semibold">Traductions existantes</Label>
                 <div className="grid grid-cols-2 gap-2 text-sm">
                   {Object.entries(editingCategory.name_translations as Record<string, string>).map(([lang, translation]) => (
                     <div key={lang} className="flex items-center gap-2">
                       <Badge variant="outline">{lang.toUpperCase()}</Badge>
                       <span className="text-muted-foreground">{translation}</span>
                     </div>
                   ))}
                 </div>
                 <p className="text-xs text-muted-foreground mt-2">
                   💡 Les traductions seront régénérées automatiquement lors de la sauvegarde
                 </p>
               </div>
             )}

             <div>
               <Label htmlFor="category_icon">Icône</Label>
               <div className="space-y-3 mt-2">
                 {/* Barre de recherche */}
                 <div className="relative">
                   <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                   <Input
                     placeholder="Rechercher une icône..."
                     value={iconSearchQuery}
                     onChange={(e) => setIconSearchQuery(e.target.value)}
                     className="pl-9"
                   />
                 </div>
                 
                 {/* Aperçu icône sélectionnée */}
                 <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                   <div 
                     className="w-12 h-12 rounded-lg flex items-center justify-center"
                     style={{ 
                       background: `linear-gradient(135deg, ${colorGradients.find(g => g.from === categoryForm.color_from)?.colors.from || '#3B82F6'}, ${colorGradients.find(g => g.to === categoryForm.color_to)?.colors.to || '#2563EB'})`
                     }}
                   >
                     {React.createElement(
                       iconMap[categoryForm.icon as keyof typeof iconMap] || Package,
                       { className: "h-6 w-6 text-white" }
                     )}
                   </div>
                   <div>
                     <div className="font-medium text-sm">{categoryForm.icon}</div>
                     <div className="text-xs text-muted-foreground">Icône sélectionnée</div>
                   </div>
                 </div>
                 
                 {/* Grille d'icônes */}
                 <div className="border rounded-lg p-3 max-h-[300px] overflow-y-auto">
                   <div className="grid grid-cols-8 gap-2">
                     {availableIcons
                       .filter(icon => 
                         iconSearchQuery === '' || 
                         icon.toLowerCase().includes(iconSearchQuery.toLowerCase())
                       )
                       .map(icon => {
                         const IconComponent = iconMap[icon as keyof typeof iconMap] || Package;
                         return (
                           <button
                             key={icon}
                             type="button"
                             onClick={() => {
                               setCategoryForm(prev => ({ ...prev, icon }));
                               setIconSearchQuery('');
                             }}
                             className={`p-3 rounded-lg border-2 hover:bg-accent transition-colors ${
                               categoryForm.icon === icon 
                                 ? 'border-primary bg-primary/10' 
                                 : 'border-border'
                             }`}
                             title={icon}
                           >
                             <IconComponent className="h-5 w-5" />
                           </button>
                         );
                       })}
                   </div>
                   {availableIcons.filter(icon => 
                     iconSearchQuery === '' || 
                     icon.toLowerCase().includes(iconSearchQuery.toLowerCase())
                   ).length === 0 && (
                     <div className="text-center py-8 text-muted-foreground text-sm">
                       Aucune icône trouvée pour "{iconSearchQuery}"
                     </div>
                   )}
                 </div>
               </div>
             </div>

             <div>
               <Label>Couleur du gradient</Label>
               <div className="grid grid-cols-10 gap-2 mt-2">
                 {colorGradients.map(gradient => (
                   <button
                     key={gradient.name}
                     type="button"
                     className={`h-10 w-full rounded-md border-2 ${
                       categoryForm.color_from === gradient.from && categoryForm.color_to === gradient.to
                         ? 'border-primary ring-2 ring-primary/20 scale-110' 
                         : 'border-border hover:scale-105'
                     } transition-transform`}
                     style={{ 
                       background: `linear-gradient(135deg, ${gradient.colors.from}, ${gradient.colors.to})`
                     }}
                     onClick={() => setCategoryForm(prev => ({ 
                       ...prev, 
                       color_from: gradient.from, 
                       color_to: gradient.to 
                     }))}
                     title={gradient.name}
                   />
                 ))}
               </div>
             </div>

             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="category_sort_order">Ordre d'affichage</Label>
                 <Input
                   id="category_sort_order"
                   type="number"
                   value={categoryForm.sort_order}
                   onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                 />
               </div>
               <div className="flex items-center space-x-2">
                 <Switch
                   id="category_active"
                   checked={categoryForm.active}
                   onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, active: checked }))}
                 />
                 <Label htmlFor="category_active">Catégorie active</Label>
               </div>
             </div>

             <DialogFooter>
               <Button type="button" variant="outline" onClick={() => setCategoryDialog(false)}>
                 Annuler
               </Button>
               <Button type="submit">
                 <Save className="h-4 w-4 mr-2" />
                 {editingCategory ? 'Modifier' : 'Créer'}
               </Button>
             </DialogFooter>
           </form>
         </DialogContent>
       </Dialog>

       {/* Content Dialog avec éditeur WYSIWYG */}
       <Dialog open={contentDialog} onOpenChange={setContentDialog}>
         <DialogContent className="max-w-4xl max-h-[90vh] bg-white/95 backdrop-blur-sm overflow-y-auto">
           <DialogHeader>
             <DialogTitle>
               {editingContent ? 'Modifier la page' : 'Nouvelle page'}
             </DialogTitle>
             <DialogDescription>
               Créez ou modifiez le contenu de votre page avec l'éditeur graphique
             </DialogDescription>
           </DialogHeader>
           <form onSubmit={handleContentSubmit} className="space-y-6">
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="content_title">Titre de la page</Label>
                 <Input
                   id="content_title"
                   value={contentForm.title}
                   onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
                   required
                 />
               </div>
               <div>
                 <Label htmlFor="content_key">Clé unique</Label>
                 <Input
                   id="content_key"
                   value={contentForm.key}
                   onChange={(e) => setContentForm(prev => ({ ...prev, key: e.target.value }))}
                   placeholder="about-us"
                   required
                 />
               </div>
             </div>

             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="content_type">Type de contenu</Label>
                 <Select 
                   value={contentForm.content_type} 
                   onValueChange={(value) => setContentForm(prev => ({ ...prev, content_type: value }))}
                 >
                   <SelectTrigger>
                     <SelectValue />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="page">Page</SelectItem>
                     <SelectItem value="article">Article</SelectItem>
                     <SelectItem value="faq">FAQ</SelectItem>
                     <SelectItem value="legal">Légal</SelectItem>
                   </SelectContent>
                 </Select>
               </div>
               <div>
                 <Label htmlFor="content_status">Statut</Label>
                 <Select 
                   value={contentForm.status} 
                   onValueChange={(value) => setContentForm(prev => ({ ...prev, status: value }))}
                 >
                   <SelectTrigger>
                     <SelectValue />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="published">Publié</SelectItem>
                     <SelectItem value="draft">Brouillon</SelectItem>
                   </SelectContent>
                 </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="content_featured_image">Image de la section (URL)</Label>
            <Input
              id="content_featured_image"
              value={contentForm.featured_image}
              onChange={(e) => setContentForm(prev => ({ ...prev, featured_image: e.target.value }))}
              placeholder="https://exemple.com/image.jpg ou /hero-marketplace.jpg"
            />
            {contentForm.featured_image && (
              <div className="mt-2 rounded-lg border overflow-hidden">
                <img src={contentForm.featured_image} alt="Aperçu de l'image" className="w-full aspect-video object-cover" />
              </div>
            )}
          </div>

          <div>
               <div className="flex items-center space-x-2 mb-4">
                 <Label>Contenu de la page</Label>
                 <div className="flex bg-gray-100 rounded-lg p-1">
                   <Button
                     type="button"
                     size="sm"
                     variant={editMode === 'visual' ? 'default' : 'ghost'}
                     onClick={() => setEditMode('visual')}
                     className="text-xs"
                   >
                     <Monitor className="h-3 w-3 mr-1" />
                     Visuel
                   </Button>
                   <Button
                     type="button"
                     size="sm"
                     variant={editMode === 'code' ? 'default' : 'ghost'}
                     onClick={() => setEditMode('code')}
                     className="text-xs"
                   >
                     <Code className="h-3 w-3 mr-1" />
                     Code
                   </Button>
                 </div>
               </div>
               
               {editMode === 'visual' ? (
                 <div className="min-h-[300px] border rounded-lg">
                   <Suspense fallback={<div className="h-[300px] bg-gray-100 animate-pulse rounded-md"></div>}>
                     <ReactQuill
                       value={contentHtml}
                       onChange={setContentHtml}
                       modules={{
                         toolbar: [
                           [{ 'header': [1, 2, 3, false] }],
                           ['bold', 'italic', 'underline', 'strike'],
                           [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                           [{ 'color': [] }, { 'background': [] }],
                           ['link', 'image'],
                           ['clean']
                         ]
                       }}
                       formats={[
                         'header', 'bold', 'italic', 'underline', 'strike',
                         'list', 'bullet', 'color', 'background', 'link', 'image'
                       ]}
                       theme="snow"
                       style={{ height: '250px', marginBottom: '50px' }}
                     />
                   </Suspense>
                 </div>
               ) : (
                 <Textarea
                   value={contentHtml}
                   onChange={(e) => setContentHtml(e.target.value)}
                   placeholder="Entrez votre code HTML ici..."
                   className="min-h-[300px] font-mono text-sm"
                 />
               )}
             </div>

             <div>
               <Label htmlFor="content_meta_title">Titre SEO</Label>
               <Input
                 id="content_meta_title"
                 value={contentForm.meta_title}
                 onChange={(e) => setContentForm(prev => ({ ...prev, meta_title: e.target.value }))}
                 placeholder="Titre optimisé pour les moteurs de recherche"
               />
             </div>

             <div>
               <Label htmlFor="content_meta_description">Description SEO</Label>
               <Textarea
                 id="content_meta_description"
                 value={contentForm.meta_description}
                 onChange={(e) => setContentForm(prev => ({ ...prev, meta_description: e.target.value }))}
                 placeholder="Description pour les moteurs de recherche (max 160 caractères)"
                 maxLength={160}
               />
             </div>

             <div>
               <Label htmlFor="content_language">Langue</Label>
               <Select 
                 value={contentForm.language} 
                 onValueChange={(value) => setContentForm(prev => ({ ...prev, language: value }))}
               >
                 <SelectTrigger>
                   <SelectValue />
                 </SelectTrigger>
                 <SelectContent>
                   <SelectItem value="fr">Français</SelectItem>
                   <SelectItem value="en">English</SelectItem>
                   <SelectItem value="lg-fr">Lingala</SelectItem>
                 </SelectContent>
               </Select>
             </div>

             <DialogFooter>
               <Button type="button" variant="outline" onClick={() => setContentDialog(false)}>
                 Annuler
               </Button>
               <Button type="submit">
                 <Save className="h-4 w-4 mr-2" />
                 {editingContent ? 'Modifier' : 'Créer'}
               </Button>
             </DialogFooter>
           </form>
          </DialogContent>
        </Dialog>
        
        {/* Footer Dialog - Moved outside Tabs to fix accessibility */}
        <Dialog open={footerDialog} onOpenChange={setFooterDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm">
            <DialogHeader>
              <DialogTitle>
                {editingFooter ? t('cms.footer.edit_section') : t('cms.footer.new_section')}
              </DialogTitle>
              <DialogDescription>
                {t('cms.footer.dialog_desc')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleFooterSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footer_title">{t('cms.footer.section_title')}</Label>
                  <Input
                    id="footer_title"
                    value={footerForm.title}
                    onChange={(e) => setFooterForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={t('cms.footer.title_placeholder')}
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <Label htmlFor="footer_key">{t('cms.footer.section_key')}</Label>
                  <Input
                    id="footer_key"
                    value={footerForm.section_key}
                    onChange={(e) => setFooterForm(prev => ({ ...prev, section_key: e.target.value }))}
                    placeholder={t('cms.footer.key_placeholder')}
                    required
                    aria-required="true"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t('cms.footer.section_links')}</Label>
                  <Button 
                    type="button" 
                    onClick={addFooterLink} 
                    variant="outline" 
                    size="sm"
                    className="min-h-[44px]"
                    aria-label={t('cms.footer.add_link')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('cms.footer.add_link')}
                  </Button>
                </div>
                
                {(footerForm.content || []).map((link: any, index: number) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end p-4 border rounded-lg bg-gray-50">
                    <div className="col-span-4">
                      <Label htmlFor={`link_title_${index}`}>{t('cms.footer.link_title')}</Label>
                      <Input
                        id={`link_title_${index}`}
                        value={link.title}
                        onChange={(e) => updateFooterLink(index, 'title', e.target.value)}
                        placeholder={t('cms.footer.link_title_placeholder')}
                        aria-label={`${t('cms.footer.link_title')} ${index + 1}`}
                      />
                    </div>
                    <div className="col-span-4">
                      <Label htmlFor={`link_url_${index}`}>{t('cms.footer.link_url')}</Label>
                      <Input
                        id={`link_url_${index}`}
                        value={link.url}
                        onChange={(e) => updateFooterLink(index, 'url', e.target.value)}
                        placeholder={t('cms.footer.link_url_placeholder')}
                        aria-label={`${t('cms.footer.link_url')} ${index + 1}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor={`link_icon_${index}`}>{t('cms.footer.link_icon')}</Label>
                      <Input
                        id={`link_icon_${index}`}
                        value={link.icon}
                        onChange={(e) => updateFooterLink(index, 'icon', e.target.value)}
                        placeholder="Info"
                        aria-label={`${t('cms.footer.link_icon')} ${index + 1}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => removeFooterLink(index)}
                        className="min-w-[44px] min-h-[44px] hover:bg-red-50 hover:text-red-600"
                        aria-label={`${t('cms.footer.remove_link')} ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="footer_sort_order">{t('cms.footer.sort_order')}</Label>
                  <Input
                    id="footer_sort_order"
                    type="number"
                    value={footerForm.sort_order}
                    onChange={(e) => setFooterForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    aria-label={t('cms.footer.sort_order')}
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <Switch
                    id="footer_active"
                    checked={footerForm.active}
                    onCheckedChange={(checked) => setFooterForm(prev => ({ ...prev, active: checked }))}
                    aria-label={t('cms.footer.section_active')}
                  />
                  <Label htmlFor="footer_active">{t('cms.footer.section_active')}</Label>
                </div>
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setFooterDialog(false)}
                  className="min-h-[44px]"
                >
                  {t('cms.footer.cancel')}
                </Button>
                <Button 
                  type="submit"
                  className="min-h-[44px]"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingFooter ? t('cms.footer.save_edit') : t('cms.footer.save_create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
   );
};